// src/components/ui/SurveyDisplay.js

import React, { useCallback, useMemo, useEffect } from 'react';
import 'survey-core/survey-core.css'; // Make sure this CSS is imported
import { Model } from 'survey-core';
import { Survey } from 'survey-react-ui';

/**
 * A reusable component to display a SurveyJS survey.
 * It takes the survey JSON and a callback for when the survey is completed.
 *
 * @param {Object} props - Component props.
 * @param {Object} props.surveyJson - The JSON definition of the survey.
 * @param {function(Object)} props.onSurveyComplete - Callback function to be called when the survey is completed,
 * @param {function(Object)} props.onSurveyHasStarted - Callback function to be called when the onSurveyStart surveyJS event,
 * @param {function(Object)} props.onPageValidation - Callback function to be called when the onValidatePage surveyJS event,
 *                                                     
 */

function SurveyDisplay({ surveyJson, surveyThemeJson, onSurveyComplete, onSurveyHasStarted, onPageValidation }) {
	// Use useMemo to create the SurveyJS Model only when surveyJson changes.
	// This prevents unnecessary re-creations of the model on re-renders,
	// which can reset survey state (e.g., entered values).
	const survey = useMemo(() => {
		if (!surveyJson) {
			return null; // Handle case where surveyJson is not yet loaded
		}
		const model = new Model(surveyJson);
		if (surveyThemeJson) {
			model.applyTheme(surveyThemeJson)
		}
		return model;
	}, [surveyJson]);

	// Use useCallback to memorize the onComplete handler.
	// This ensures the function reference is stable, preventing unnecessary re-renders
	// of child components that might depend on this prop.
	const handleSurveyComplete = useCallback((sender, options) => {
		// Call the parent-provided callback with the survey results
		if (onSurveyComplete && typeof onSurveyComplete === 'function') {
			onSurveyComplete(sender, options);
		}
	}, [onSurveyComplete]);

	const handleSurveyStarted = useCallback(() => {
		// Call the parent-provided callback with the survey results
		if (onSurveyHasStarted && typeof onSurveyHasStarted === 'function') {
			onSurveyHasStarted();
		}
	}, [onSurveyHasStarted]);

	const handlePageValidation = useCallback((sender, options) => {
		// Call the parent-provided callback with the survey results
		if (onPageValidation && typeof onPageValidation === 'function') {
			onPageValidation(sender, options);
		}
	}, [onPageValidation]);

	// Attach the onComplete event listener
	// This should be done only once when the survey model is created or changes.
	// We're doing it inside useMemo, but sometimes it's cleaner to do it with useEffect
	// if the listener itself has dependencies. Here, it's fine.
	useEffect(() => {
		if (survey) {
			survey.onComplete.add(handleSurveyComplete);
			survey.onStarted.add(handleSurveyStarted)
			survey.onValidatePage.add(handlePageValidation)
			return () => {
				survey.onComplete.remove(handleSurveyComplete);
				survey.onStarted.remove(handleSurveyStarted)
				survey.onValidatePage.remove(handlePageValidation)
			};
		}
	}, [survey, handleSurveyComplete, handleSurveyStarted, handlePageValidation]); // Dependencies for this effect

	if (!survey) {
	// You could return a loading indicator here if surveyJson might load asynchronously
		return <div className="text-center">Loading survey structure...</div>;
	}

	return (
		<div className="survey-display-container">
			<Survey model={survey} />
		</div>
	);
}

export default SurveyDisplay;
